/**
 * DeactivateRoleModal — Rol pasifleştirme uyarı modal'ı (Iteration 2)
 *
 * Backend:
 *   GET    /api/admin/roles/{id}/impact   → preview (etki analizi)
 *   DELETE /api/admin/roles/{id}          → soft delete (pasifleştir)
 *
 * Tasarım kuralı: pasifleştirme atomik basit kalır; otomatik personel
 * transferi veya otomatik approval reroute YAPILMAZ. Sistemi aksatmamak
 * için kullanıcı bilgilendirilir ve kalıcı çözüm için Vekalet Yönetimi
 * altyapısına yönlendirilir. Reaktive edilince personeller yetkilerini
 * geri kazanır → tam geri alınabilir.
 *
 * Pattern: DeactivatePopupModal.jsx (personel pasifleştirme) — aynı
 * fetch akışı, kart layout'u, footer butonları.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertTriangle, Users, Clock, Loader2, PowerOff,
  Info, RotateCcw, ShieldOff, ChevronDown, ChevronRight, Network,
} from "lucide-react";
import { toast } from "sonner";

import apiClient from "@/api/client";
import SubordinateChainTree from "@/components/admin/SubordinateChainTree";


function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}


export default function DeactivateRoleModal({
  role,
  onClose,
  onComplete,
  onNavigateToOrganization,
}) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  // Default-collapsed personel listesi: kullanıcı isteyince açar.
  const [personnelListOpen, setPersonnelListOpen] = useState(false);
  // Single-open accordion: aynı anda sadece bir personelin alt zinciri açık.
  const [openPersonnelId, setOpenPersonnelId] = useState(null);
  // Alt zincir cache'i — aynı personeli kapatıp tekrar açınca fetch atlanır.
  const [chainCache, setChainCache] = useState(() => new Map());
  // Retry tetikleyici — "Tekrar dene" butonu effect'i yeniden çağırır.
  const [retryKey, setRetryKey] = useState(0);

  const handleChainLoaded = useCallback((id, chainData) => {
    setChainCache((prev) => {
      const next = new Map(prev);
      next.set(id, chainData);
      return next;
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    apiClient
      .get(`/admin/roles/${role.id}/impact`)
      .then((res) => {
        if (mounted) setImpact(res.data);
      })
      .catch((err) => {
        if (mounted) {
          const msg = err?.response?.data?.error?.message || err.message;
          setError(msg);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
    // retryKey dahil: "Tekrar dene" → useEffect tekrar tetiklenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.id, retryKey]);

  const handleDeactivate = async () => {
    setExecuting(true);
    try {
      await apiClient.delete(`/admin/roles/${role.id}`);
      toast.success(`'${role.display_name}' rolü pasifleştirildi.`);
      // Defensive: onComplete genelde modal'ı kapatır (setDeactivateTarget(null)
      // tetikler), onClose ek çağrı no-op olabilir ama parent onComplete'i
      // implement etmezse modal'ın kapanmasını garantiler. Idempotent.
      onComplete?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message;
      toast.error("Pasifleştirilemedi: " + msg);
    } finally {
      setExecuting(false);
    }
  };

  const affectedCount = impact?.affected_personnel_count ?? 0;
  const pendingCount = impact?.pending_approvals_count ?? 0;
  const personnelShown = impact?.affected_personnel ?? [];
  const approvalsShown = impact?.pending_approvals_sample ?? [];
  const personnelRemaining = Math.max(0, affectedCount - personnelShown.length);
  const approvalsRemaining = Math.max(0, pendingCount - approvalsShown.length);

  return (
    <Dialog open onOpenChange={(open) => !open && !executing && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldOff className="h-5 w-5" />
            Rolü Pasifleştir: {role.display_name}
          </DialogTitle>
          <DialogDescription>
            Bu işlem geri alınabilir — pasif rolleri sonra tek tıkla tekrar aktif edebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {loading && (
            <div className="text-center py-6">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
              <p className="text-sm text-gray-500 mt-2">Etki analizi yükleniyor...</p>
            </div>
          )}

          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800 flex items-center justify-between gap-2">
                <span>Etki analizi alınamadı: {error}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => setRetryKey((k) => k + 1)}
                >
                  Tekrar Dene
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {impact && !loading && (
            <>
              {/* İstatistik kartları */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded p-3 bg-orange-50 border-orange-200">
                  <div className="flex items-center gap-2 text-orange-700 text-sm font-semibold mb-1">
                    <Users className="h-4 w-4" />
                    Etkilenen Personel
                  </div>
                  <div className="text-2xl font-bold text-orange-700">
                    {affectedCount}
                  </div>
                  <div className="text-xs text-orange-600">
                    Bu rolde aktif kişi
                  </div>
                </div>
                <div className="border rounded p-3 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 text-blue-700 text-sm font-semibold mb-1">
                    <Clock className="h-4 w-4" />
                    Bekleyen Onay
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    {pendingCount}
                  </div>
                  <div className="text-xs text-blue-600">
                    Bu kişiler üzerinde
                  </div>
                </div>
              </div>

              {/* Personel listesi — default collapsed; aç/kapat toggle ile */}
              {affectedCount > 0 && (
                <div className="border rounded">
                  <button
                    type="button"
                    onClick={() => setPersonnelListOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded"
                  >
                    <span className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                      <Users className="h-3 w-3" />
                      {affectedCount} personel etkilenecek
                      {affectedCount > personnelShown.length && (
                        <span className="text-gray-400 font-normal">
                          (ilk {personnelShown.length})
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1 text-[11px] text-gray-500">
                      {personnelListOpen ? "gizle" : "kimler olduğunu gör"}
                      {personnelListOpen
                        ? <ChevronDown className="h-3 w-3" />
                        : <ChevronRight className="h-3 w-3" />}
                    </span>
                  </button>

                  {personnelListOpen && (
                    <div className="border-t px-2 py-1 bg-white">
                      {personnelShown.map((p) => {
                        const isOpen = openPersonnelId === p.id;
                        const canExpand = !!p.has_subordinates;
                        return (
                          <div key={p.id} className="border-b last:border-b-0">
                            <div
                              className={`flex items-center gap-2 py-1.5 px-1 rounded ${
                                canExpand ? "cursor-pointer hover:bg-gray-50" : ""
                              }`}
                              onClick={
                                canExpand
                                  ? () => setOpenPersonnelId(isOpen ? null : p.id)
                                  : undefined
                              }
                              role={canExpand ? "button" : undefined}
                              tabIndex={canExpand ? 0 : undefined}
                              onKeyDown={
                                canExpand
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setOpenPersonnelId(isOpen ? null : p.id);
                                      }
                                    }
                                  : undefined
                              }
                            >
                              {canExpand ? (
                                isOpen
                                  ? <ChevronDown className="h-3 w-3 text-gray-500 flex-shrink-0" />
                                  : <ChevronRight className="h-3 w-3 text-gray-500 flex-shrink-0" />
                              ) : (
                                <span className="w-3 flex-shrink-0" />
                              )}
                              <Badge variant="outline" className="text-[10px] font-mono">
                                {p.role || "—"}
                              </Badge>
                              <span className="text-xs text-gray-800 flex-1 truncate">
                                {p.name}
                              </span>
                            </div>
                            {isOpen && canExpand && (
                              <div className="mt-0.5 mb-2 ml-4 pl-2 border-l-2 border-purple-200">
                                <div className="text-[10px] text-gray-500 italic px-1 py-0.5 mb-1 flex items-start gap-1">
                                  <Info className="h-2.5 w-2.5 mt-0.5 flex-shrink-0" />
                                  <span>
                                    Alt zincir bağlam için gösteriliyor — bu kişiler pasifleştirmeden etkilenmez.
                                  </span>
                                </div>
                                <SubordinateChainTree
                                  personnelId={p.id}
                                  cachedChain={chainCache.get(p.id)}
                                  onChainLoaded={handleChainLoaded}
                                  skipRoot
                                  compact
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {personnelRemaining > 0 && (
                        <div className="text-xs text-gray-500 italic py-1.5 px-1">
                          ... ve {personnelRemaining} kişi daha.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Bekleyen onay özeti */}
              {pendingCount > 0 && (
                <div className="border rounded p-3">
                  <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Askıda Kalacak Onaylar
                    {approvalsRemaining > 0 && (
                      <span className="text-gray-400">
                        (ilk {approvalsShown.length} gösteriliyor)
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {approvalsShown.map((a, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-xs py-0.5"
                      >
                        <Badge variant="secondary" className="text-xs">
                          {a.type}
                        </Badge>
                        <span className="text-gray-800 flex-1">{a.requester_name}</span>
                        <span className="text-gray-500 font-mono">{formatDate(a.created_at)}</span>
                      </div>
                    ))}
                    {approvalsRemaining > 0 && (
                      <div className="text-xs text-gray-500 italic pt-1">
                        ... ve {approvalsRemaining} onay daha.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Etki uyarısı (sarı) */}
              {affectedCount > 0 && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900 text-sm">
                    <strong>Ne olacak?</strong>{" "}
                    Bu {affectedCount} personel sisteme giriş yapabilir ama
                    bu rolün yetkilerini kullanamaz.{" "}
                    {pendingCount > 0 && (
                      <>
                        Bekleyen <strong>{pendingCount} onay askıda kalır</strong>{" "}
                        (reddedilmez, silinmez).
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Çözüm önerisi (mavi) — sadece bekleyen onay varsa */}
              {pendingCount > 0 && (
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-900 text-sm">
                    <strong>Onayları başkasına devretmek için:</strong>{" "}
                    Pasifleştirmeden önce <strong>Vekalet Yönetimi</strong>{" "}
                    panelinden ilgili kişilere vekalet atayabilirsiniz.
                  </AlertDescription>
                </Alert>
              )}

              {/* Hiyerarşi düzenleme önerisi (indigo) — sadece etkilenen personel varsa */}
              {affectedCount > 0 && onNavigateToOrganization && (
                <Alert className="bg-indigo-50 border-indigo-200">
                  <Network className="h-4 w-4 text-indigo-700" />
                  <AlertDescription className="text-indigo-900 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span>Hiyerarşiyi düzenlemek ister misiniz?</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 whitespace-nowrap"
                        onClick={() => {
                          onNavigateToOrganization?.();
                          onClose?.();
                        }}
                        disabled={executing}
                      >
                        Önce hiyerarşiyi düzenle
                      </Button>
                    </div>
                    <p className="text-xs text-indigo-700 mt-1 italic">
                      Bu işlem modal'ı kapatır; düzenleme sonrası rol pasifleştirme adımını tekrar başlatmanız gerekir.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Geri alma bilgisi (yeşil) — her zaman */}
              <Alert className="bg-green-50 border-green-200">
                <RotateCcw className="h-4 w-4 text-green-700" />
                <AlertDescription className="text-green-900 text-sm">
                  <strong>Geri almak için:</strong>{" "}
                  "Pasif Roller" sekmesine geçin ve <strong>Aktifleştir</strong>{" "}
                  butonuna tıklayın — tüm yetkiler ve onaylar eski hâline döner.
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={executing}
          >
            İptal
          </Button>
          <Button
            onClick={handleDeactivate}
            disabled={executing || loading || !!error}
            className="bg-red-600 hover:bg-red-700"
          >
            {executing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Pasifleştiriliyor...
              </>
            ) : (
              <>
                <PowerOff className="h-4 w-4 mr-2" />
                Evet, Pasifleştir
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
